import { useEffect, useState, useMemo, useCallback } from 'react';
import type { FC, ChangeEvent } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Divider,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import Page from 'src/components/Page';
import {
  ProfileGeneralSettings,
  ProfileSecuritySettings,
} from 'src/components/dashboard/profile';
import useAuth from 'src/hooks/useAuth';
import ChevronLeftIcon from 'src/icons/ChevronLeft';
import { PATH_DASHBOARD } from 'src/routes/paths';
import ProfileChangeNumberDialog from 'src/components/dashboard/profile/ProfileChangeNumberDialog';
import { useSendChangePhoneNumberVerificationCodeMutation } from 'src/graphql/idr/user/mutations/sendChangePhoneNumberVerificationCode.generated';
import { useVerifyUserForPhoneNumberMutation } from 'src/graphql/idr/user/mutations/verifyUserForPhoneNumber.generated';
import { useChangePhoneNumberMutation } from 'src/graphql/idr/user/mutations/changePhoneNumber.generated';
import toast from 'react-hot-toast';
import { parseErrorMsg } from 'src/utils/errors';

const tabs = [
  { label: 'مشخصات فردی', value: 'general' },
  { label: 'تغییر رمز‌عبور', value: 'security' },
];

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const Profile: FC<{ defaultTab?: string }> = ({
  defaultTab,
}: {
  defaultTab?: string;
}) => {
  const query = useQuery();
  const tabParam = query.get('tab');
  const { initialize } = useAuth();
  const [currentTab, setCurrentTab] = useState<string>(defaultTab || 'general');
  const [changePhoneNumberOpen, setChangePhoneNumberOpen] =
    useState<boolean>(false);
  const [changedPhoneNumber, setChangedPhoneNumber] = useState<string>(null);

  const { mutate: sendCodeMutation } =
    useSendChangePhoneNumberVerificationCodeMutation();
  const { mutate: verifyCodeMutation } = useVerifyUserForPhoneNumberMutation();
  const { mutate: changePhoneNumberMutation } = useChangePhoneNumberMutation();

  const handleTabsChange = (event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  const closeChangePhoneNumberDialog = useCallback(() => {
    setChangePhoneNumberOpen(false);
  }, [setChangePhoneNumberOpen]);

  const openChangePhoneNumberDialog = useCallback(() => {
    setChangePhoneNumberOpen(true);
  }, [setChangePhoneNumberOpen]);

  const changePhoneNumber = useCallback(() => {
    changePhoneNumberMutation(
      { data: { dto: { phoneNumber: changedPhoneNumber } } },
      {
        onSuccess: () => {
          toast.success('شماره تماس با موفقیت ویرایش شد.');
          initialize();
        },
        onError: (error: any) => {
          toast.error(parseErrorMsg(error));
        },
      }
    );
  }, [changedPhoneNumber]);

  const sendCode = useCallback((phoneNumber) => {
    sendCodeMutation(
      { data: { dto: { userName: phoneNumber } } },
      {
        onSuccess: (data: any) => {
          console.log(
            'Verification code: ',
            data.userMutations.sendChangePhoneNumberVerificationCode.listDto
              .items[0].verificationCode
          );
          toast.success('کد فعال‌سازی با موفقیت ارسال شد');
          openChangePhoneNumberDialog();
        },
        onError: (error: any) => {
          toast.error(parseErrorMsg(error));
        },
      }
    );
  }, []);

  const onChangePhoneNumber = useCallback(
    (phoneNumber: string) => {
      setChangedPhoneNumber(phoneNumber);
      sendCode(phoneNumber);
    },
    [sendCode, setChangedPhoneNumber]
  );

  const verifyCode = async (code: string) => {
    verifyCodeMutation(
      { data: { dto: { code } } },
      {
        onSuccess: () => {
          closeChangePhoneNumberDialog();
          changePhoneNumber();
        },
        onError: (error: any) => {
          toast.error(parseErrorMsg(error));
          closeChangePhoneNumberDialog();
        },
      }
    );
  };

  useEffect(() => {
    setCurrentTab(tabParam || defaultTab || 'general');
  }, [tabParam, defaultTab]);

  return (
    <Page title="هیئت آنلاین | پروفایل">
      {!defaultTab && (
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<ChevronLeftIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            color="textPrimary"
            component={RouterLink}
            to={PATH_DASHBOARD.overview}
            variant="subtitle2"
            sx={{ textDecoration: 'unset' }}
          >
            داشبورد
          </Link>
          <Typography color="textSecondary" variant="subtitle2">
            پروفایل
          </Typography>
        </Breadcrumbs>
      )}
      <Box
        sx={{
          padding: '32px 32px 28px 32px',
          backgroundColor: 'background2.neutral',
          border: '1px solid',
          borderColor: 'background2.disabled',
          borderRadius: '16px',
        }}
      >
        <Tabs
          indicatorColor="primary"
          onChange={handleTabsChange}
          scrollButtons="auto"
          textColor="primary"
          value={currentTab}
          variant="scrollable"
        >
          {tabs
            .filter((tab) => {
              if (!defaultTab) return true;
              return tab.value === defaultTab;
            })
            .map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
        </Tabs>
        <Divider />
        <Box sx={{ mt: 3 }}>
          {currentTab === 'general' && (
            <ProfileGeneralSettings onChangePhoneNumber={onChangePhoneNumber} />
          )}
          {currentTab === 'security' && <ProfileSecuritySettings />}
        </Box>
      </Box>
      <ProfileChangeNumberDialog
        open={changePhoneNumberOpen}
        onClose={closeChangePhoneNumberDialog}
        onVerifyCode={verifyCode}
        onReSendCode={sendCode}
      />
    </Page>
  );
};

export default Profile;
